import React, { useState, useEffect, useRef } from 'react';
import { Printer, Search, Users, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/api';

// =====================================================
// 8 PROFESSIONAL I-CARD TEMPLATES (User Selected: 2,3,4,5,6,8,9,10)
// =====================================================

const ICardTemplates = {
  // Template 2: Blue Curve (Vertical) - Curved Header
  'blue-curve': {
    id: 'blue-curve',
    name: 'Blue Curve',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif;">
        <!-- Header -->
        <div style="height: 95px; background: linear-gradient(180deg, #1e40af 0%, #3b82f6 100%); position: relative; text-align: center; padding-top: 10px;">
          <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 5px;">
            ${settings.college_logo ? `<img src="${settings.college_logo}" style="height: 30px; border-radius: 50%; background: white; padding: 2px;">` : '<div style="width:30px;height:30px;background:rgba(255,255,255,0.2);border-radius:50%;"></div>'}
          </div>
          <div style="color: white; font-weight: 700; font-size: 12px;">${settings.college_name || 'School Name'}</div>
          <div style="color: rgba(255,255,255,0.9); font-size: 7px;">(Govt. Recognised)</div>
          <div style="color: rgba(255,255,255,0.8); font-size: 6px; padding: 0 15px;">${settings.college_address?.substring(0, 50) || 'Address'}</div>
          <div style="color: #fbbf24; font-size: 8px; font-weight: 600; margin-top: 2px;">📞 ${settings.emergency_contact || '1234567890'}</div>
          <svg viewBox="0 0 200 30" style="position: absolute; bottom: -1px; left: 0; width: 100%;">
            <path d="M0,15 Q100,35 200,15 L200,30 L0,30 Z" fill="white"/>
          </svg>
        </div>
        
        <!-- Photo -->
        <div style="margin-top: -15px; position: relative; z-index: 10; text-align: center;">
          <div style="width: 65px; height: 80px; border: 3px solid white; box-shadow: 0 3px 10px rgba(0,0,0,0.2); background: #f3f4f6; margin: 0 auto; overflow: hidden;">
            ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:9px;">Photo</div>'}
          </div>
        </div>
        
        <!-- Name -->
        <div style="text-align: center; font-size: 13px; font-weight: 700; color: #1e293b; margin: 8px 0;">${data.student?.full_name || 'Student Name'}</div>
        
        <!-- Info Table -->
        <div style="padding: 0 15px; font-size: 8px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 4px 0; color: #666; width: 45%;">Father's Name</td><td style="padding: 4px 0; font-weight: 500;">${data.student?.father_name || 'N/A'}</td></tr>
            <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 4px 0; color: #666;">Mother's Name</td><td style="padding: 4px 0; font-weight: 500;">${data.student?.mother_name || 'N/A'}</td></tr>
            <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 4px 0; color: #666;">D.O.B.</td><td style="padding: 4px 0; font-weight: 500;">${data.student?.dob || 'N/A'}</td></tr>
            <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 4px 0; color: #666;">Contact No.</td><td style="padding: 4px 0; font-weight: 500;">${data.student?.phone || 'N/A'}</td></tr>
          </table>
        </div>
        
        <!-- Footer -->
        <div style="position: absolute; bottom: 8px; left: 12px; right: 12px; display: flex; justify-content: space-between; align-items: flex-end;">
          <div style="font-size: 8px;"><span style="color: #666;">Course:</span> <strong style="color: #1e40af;">${data.student?.course || 'N/A'}</strong></div>
          <div style="text-align: center;">
            ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 20px;">` : '<div style="width:50px;border-top:1px solid #333;margin-bottom:2px;"></div>'}
            <div style="font-size: 6px; color: #1e40af;">Principal Sign.</div>
          </div>
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; padding: 12px; position: relative;">
        <div style="text-align: center; font-weight: 700; color: #1e40af; font-size: 11px; margin-bottom: 10px; border-bottom: 2px solid #1e40af; padding-bottom: 6px;">TERMS & CONDITIONS</div>
        <div style="font-size: 8px; color: #333; line-height: 1.6;">
          ${(settings.rules_regulations || '1. This card is non-transferable.\n2. Report loss immediately.\n3. Card must be carried at all times.').split('\n').map((rule, i) => `<div style="margin-bottom: 5px;">${rule}</div>`).join('')}
        </div>
        <div style="position: absolute; bottom: 70px; left: 12px; right: 12px; font-size: 8px;">
          <div><strong>Session:</strong> ${data.student?.session || 'N/A'}</div>
          <div><strong>Blood Group:</strong> ${data.student?.blood_group || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 15px; left: 50%; transform: translateX(-50%);">
          ${data.qr_code ? `<img src="${data.qr_code}" style="width: 50px; height: 50px;">` : '<div style="width:50px;height:50px;background:#f3f4f6;border:1px solid #ddd;"></div>'}
        </div>
      </div>
    `
  },

  // Template 3: Purple Magenta - Wave Design
  'purple-magenta': {
    id: 'purple-magenta',
    name: 'Purple Magenta',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif;">
        <!-- Header -->
        <div style="height: 100px; background: linear-gradient(135deg, #9333ea 0%, #c026d3 50%, #e879f9 100%); position: relative; text-align: center; padding: 12px;">
          ${settings.college_logo ? `<div style="width:30px;height:30px;background:rgba(255,255,255,0.2);border-radius:50%;margin:0 auto 5px;overflow:hidden;"><img src="${settings.college_logo}" style="width:100%;height:100%;object-fit:cover;"></div>` : '<div style="width:30px;height:30px;background:rgba(255,255,255,0.2);border-radius:50%;margin:0 auto 5px;"></div>'}
          <div style="color: white; font-size: 11px; font-weight: 700;">${settings.college_name || 'SCHOOL NAME'}</div>
          <div style="color: rgba(255,255,255,0.9); font-size: 7px;">Excellence in Education</div>
          <svg viewBox="0 0 200 40" style="position: absolute; bottom: -20px; left: 0; width: 100%;">
            <path d="M0,20 Q100,45 200,20 L200,40 L0,40 Z" fill="white"/>
          </svg>
        </div>
        
        <!-- Photo Container -->
        <div style="position: relative; z-index: 10; margin-top: -25px; text-align: center;">
          <div style="width: 70px; height: 85px; border: 4px solid #9333ea; background: #f3f4f6; margin: 0 auto; box-shadow: 0 5px 15px rgba(147,51,234,0.3); overflow: hidden;">
            ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:9px;">Photo</div>'}
          </div>
        </div>
        
        <!-- Info Section -->
        <div style="padding: 10px 15px;">
          <table style="width: 100%; font-size: 9px;">
            <tr><td style="color: #666; padding: 3px 0; width: 45%;">Reg No</td><td style="padding: 3px 0;">: ${data.student?.student_no || '123456'}</td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Student ID</td><td style="padding: 3px 0;">: ${data.student?.student_no?.slice(-6) || '1234'}</td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Student Name</td><td style="padding: 3px 0;">: <strong>${data.student?.full_name || 'Name'}</strong></td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Father Name</td><td style="padding: 3px 0;">: ${data.student?.father_name || 'N/A'}</td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Course</td><td style="padding: 3px 0;">: ${data.student?.course || 'N/A'}</td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Session</td><td style="padding: 3px 0;">: ${data.student?.session || 'N/A'}</td></tr>
          </table>
        </div>
        
        <!-- Footer -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0; background: linear-gradient(135deg, #9333ea, #c026d3); padding: 6px 12px; display: flex; justify-content: space-between; color: white; font-size: 7px;">
          <span>📞 ${settings.emergency_contact || '123-456-7890'}</span>
          <span>Valid: ${data.valid_until?.substring(0,4) || '2027'}</span>
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; position: relative;">
        <div style="background: linear-gradient(135deg, #9333ea, #c026d3); padding: 10px; text-align: center;">
          <div style="color: white; font-weight: 700; font-size: 11px;">RULES & REGULATIONS</div>
        </div>
        <div style="padding: 12px; font-size: 8px; line-height: 1.6;">
          ${(settings.rules_regulations || '').split('\n').map(rule => `<div style="margin-bottom: 6px; padding-left: 12px; position: relative;"><span style="position: absolute; left: 0; color: #9333ea;">•</span>${rule}</div>`).join('')}
        </div>
        <div style="padding: 0 12px; font-size: 8px; margin-top: 10px;">
          <div><strong style="color: #9333ea;">Blood:</strong> ${data.student?.blood_group || 'N/A'}</div>
          <div><strong style="color: #9333ea;">Email:</strong> ${data.student?.email || 'N/A'}</div>
          <div><strong style="color: #9333ea;">Phone:</strong> ${data.student?.phone || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 55px; right: 12px; text-align: center;">
          ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 20px;">` : '<div style="border-top: 1px solid #333; width: 50px; margin-bottom: 2px;"></div>'}
          <div style="font-size: 6px; color: #666;">SIGNATURE</div>
          <div style="font-size: 6px;">${settings.principal_name || 'Principal'}</div>
        </div>
        <div style="position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);">
          ${data.qr_code ? `<img src="${data.qr_code}" style="width: 45px; height: 45px;">` : ''}
        </div>
        <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 15px; background: linear-gradient(135deg, #9333ea, #c026d3);"></div>
      </div>
    `
  },

  // Template 4: Red Corporate - Circle Photo
  'red-corporate': {
    id: 'red-corporate',
    name: 'Red Corporate',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif;">
        <!-- Header with clip-path -->
        <div style="background: #dc2626; padding: 12px; text-align: center; clip-path: polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%); padding-bottom: 25px;">
          <div style="color: white; font-size: 12px; font-weight: 700;">${settings.college_name || 'ABC HIGH SCHOOL'}</div>
          <div style="color: rgba(255,255,255,0.9); font-size: 7px;">CBSE Affiliated</div>
        </div>
        
        <!-- Photo Section -->
        <div style="margin-top: -20px; text-align: center; position: relative; z-index: 10;">
          <div style="width: 65px; height: 65px; border: 4px solid #dc2626; border-radius: 50%; background: #f3f4f6; margin: 0 auto; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.15);">
            ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:8px;">Photo</div>'}
          </div>
        </div>
        
        <!-- Name & Role -->
        <div style="text-align: center; font-size: 12px; font-weight: 700; color: #1e293b; margin: 8px 0 3px;">${data.student?.full_name || 'Student Name'}</div>
        <div style="text-align: center; font-size: 8px; color: #dc2626; margin-bottom: 8px;">${data.student?.course || 'Class XII - Science'}</div>
        
        <!-- Info Grid -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 0 12px; font-size: 8px;">
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">ID No</div><div style="font-weight: 600;">${data.student?.student_no || '2024001'}</div></div>
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">Roll No</div><div style="font-weight: 600;">${data.student?.roll_no || '25'}</div></div>
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">DOB</div><div style="font-weight: 600;">${data.student?.dob || '10/08/05'}</div></div>
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">Blood</div><div style="font-weight: 600;">${data.student?.blood_group || 'A+'}</div></div>
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">Session</div><div style="font-weight: 600;">${data.student?.session || '2024-25'}</div></div>
          <div style="background: #fef2f2; padding: 6px; border-radius: 4px;"><div style="color: #dc2626; font-weight: 500; font-size: 7px;">Phone</div><div style="font-weight: 600;">${data.student?.phone?.slice(-10) || '9876543210'}</div></div>
        </div>
        
        <!-- Footer Wave -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0;">
          <svg viewBox="0 0 204 25" preserveAspectRatio="none" style="width:100%;height:25px;">
            <path d="M0,15 Q50,0 102,15 T204,15 L204,25 L0,25 Z" fill="#dc2626"/>
          </svg>
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; position: relative;">
        <div style="position: absolute; top: 0; right: 0; width: 0; height: 0; border-top: 25px solid #dc2626; border-left: 25px solid transparent;"></div>
        <div style="position: absolute; top: 0; left: 0; width: 0; height: 0; border-top: 25px solid #dc2626; border-right: 25px solid transparent;"></div>
        <div style="padding: 30px 15px 15px; text-align: center;">
          <div style="font-weight: 700; font-size: 11px; color: #1f2937; margin-bottom: 10px;">TERMS AND CONDITIONS</div>
          <div style="font-size: 7px; color: #666; text-align: left; line-height: 1.5;">
            ${(settings.rules_regulations || '').split('\n').slice(0, 4).map(rule => `<div style="margin-bottom: 4px;">${rule}</div>`).join('')}
          </div>
        </div>
        <div style="padding: 0 15px; font-size: 8px;">
          <table style="width: 100%;">
            <tr><td style="color: #666; padding: 3px 0;">Father's Name</td><td style="padding: 3px 0;">: ${data.student?.father_name || 'N/A'}</td></tr>
            <tr><td style="color: #666; padding: 3px 0;">Address</td><td style="padding: 3px 0; font-size: 7px;">: ${data.student?.address?.substring(0, 30) || settings.college_address?.substring(0, 30) || 'N/A'}</td></tr>
          </table>
        </div>
        <div style="position: absolute; bottom: 55px; right: 15px; text-align: center;">
          ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 22px;">` : '<div style="font-style: italic; font-size: 12px; color: #1e40af;">Signature</div>'}
          <div style="font-size: 6px; font-weight: 600;">${settings.principal_name || 'Principal'}</div>
        </div>
        <div style="position: absolute; bottom: 50px; left: 15px;">
          ${data.qr_code ? `<img src="${data.qr_code}" style="width: 40px; height: 40px;">` : ''}
        </div>
        <div style="position: absolute; bottom: 0; left: 0; right: 0; background: #dc2626; padding: 8px; text-align: center;">
          <div style="color: white; font-weight: 700; font-size: 9px;">${settings.college_name || 'ABC HIGH SCHOOL'}</div>
          <div style="color: rgba(255,255,255,0.9); font-size: 6px;">📞 ${settings.emergency_contact || 'Contact Number'}</div>
        </div>
      </div>
    `
  },

  // Template 5: Navy Gold Premium
  'navy-gold': {
    id: 'navy-gold',
    name: 'Navy Gold Premium',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #1e3a5f 0%, #1e40af 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif; color: white;">
        <!-- Gold Line -->
        <div style="height: 3px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
        
        <!-- Header -->
        <div style="padding: 12px; text-align: center; border-bottom: 2px solid #fbbf24;">
          <div style="width: 35px; height: 35px; background: rgba(255,255,255,0.15); border-radius: 50%; margin: 0 auto 5px; border: 2px solid #fbbf24; overflow: hidden;">
            ${settings.college_logo ? `<img src="${settings.college_logo}" style="width:100%;height:100%;object-fit:cover;">` : ''}
          </div>
          <div style="font-size: 11px; font-weight: 700;">${settings.college_name || 'NATIONAL INSTITUTE'}</div>
          <div style="color: #fbbf24; font-size: 8px;">OF TECHNOLOGY & SCIENCE</div>
        </div>
        
        <!-- Photo -->
        <div style="width: 65px; height: 80px; border: 3px solid white; background: rgba(255,255,255,0.1); margin: 12px auto; overflow: hidden;">
          ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.5);font-size:9px;">Photo</div>'}
        </div>
        
        <!-- Name & ID -->
        <div style="text-align: center; font-size: 12px; font-weight: 700;">${data.student?.full_name || 'Student Name'}</div>
        <div style="text-align: center; background: #fbbf24; color: #1e3a5f; font-size: 8px; font-weight: 600; padding: 3px 12px; border-radius: 10px; width: fit-content; margin: 5px auto;">${data.student?.student_no || 'STU-NIT-2024006'}</div>
        
        <!-- Info -->
        <div style="padding: 8px 15px;">
          <table style="width: 100%; font-size: 8px;">
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.15);"><td style="opacity: 0.8; padding: 3px 0; width: 45%;">Program</td><td style="padding: 3px 0;">${data.student?.course || 'B.Tech'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.15);"><td style="opacity: 0.8; padding: 3px 0;">Branch</td><td style="padding: 3px 0;">${data.student?.branch || 'ECE'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.15);"><td style="opacity: 0.8; padding: 3px 0;">Batch</td><td style="padding: 3px 0;">${data.student?.session || '2024-28'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.15);"><td style="opacity: 0.8; padding: 3px 0;">Blood</td><td style="padding: 3px 0;">${data.student?.blood_group || 'O-'}</td></tr>
          </table>
        </div>
        
        <!-- Footer -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0; background: #fbbf24; padding: 5px; text-align: center; color: #1e3a5f; font-size: 6px; font-weight: 600;">
          Excellence • Innovation • Leadership
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #1e3a5f 0%, #1e40af 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; color: white; position: relative;">
        <div style="height: 3px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
        <div style="padding: 15px; text-align: center;">
          <div style="color: #fbbf24; font-weight: 700; font-size: 11px; margin-bottom: 10px; border-bottom: 1px solid rgba(251,191,36,0.3); padding-bottom: 6px;">RULES & REGULATIONS</div>
          <div style="font-size: 7px; line-height: 1.6; text-align: left; opacity: 0.9;">
            ${(settings.rules_regulations || '').split('\n').map(rule => `<div style="margin-bottom: 5px;">• ${rule}</div>`).join('')}
          </div>
        </div>
        <div style="padding: 0 15px; font-size: 8px;">
          <div><strong style="color: #fbbf24;">Emergency:</strong> ${settings.emergency_contact || 'N/A'}</div>
          <div><strong style="color: #fbbf24;">Address:</strong> ${settings.college_address?.substring(0, 40) || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 15px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 15px;">
          ${data.qr_code ? `<div style="width: 40px; height: 40px; background: white; border-radius: 4px; overflow: hidden;"><img src="${data.qr_code}" style="width: 100%; height: 100%;"></div>` : ''}
          <div style="text-align: center;">
            ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 20px; filter: brightness(0) invert(1);">` : '<div style="width: 50px; border-top: 1px solid #fbbf24; margin-bottom: 2px;"></div>'}
            <div style="font-size: 6px; color: #fbbf24;">${settings.principal_name || 'Director'}</div>
          </div>
        </div>
      </div>
    `
  },

  // Template 6: Green Modern - Corner Accent
  'green-modern': {
    id: 'green-modern',
    name: 'Green Modern',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #064e3b 0%, #059669 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif; color: white;">
        <!-- Corner Accent -->
        <div style="position: absolute; top: 0; right: 0; width: 50px; height: 50px; background: #fbbf24; clip-path: polygon(100% 0, 0 0, 100% 100%);"></div>
        
        <!-- Header -->
        <div style="padding: 15px; text-align: center;">
          <div style="width: 40px; height: 40px; background: rgba(255,255,255,0.15); border-radius: 50%; margin: 0 auto 8px; overflow: hidden;">
            ${settings.college_logo ? `<img src="${settings.college_logo}" style="width:100%;height:100%;object-fit:cover;">` : ''}
          </div>
          <div style="font-size: 11px; font-weight: 700;">${settings.college_name || 'GREEN VALLEY SCHOOL'}</div>
          <div style="font-size: 7px; opacity: 0.9;">Nurturing Future Leaders</div>
        </div>
        
        <!-- Photo -->
        <div style="width: 65px; height: 80px; border: 3px solid #fbbf24; background: rgba(255,255,255,0.1); margin: 5px auto 10px; overflow: hidden;">
          ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.5);font-size:9px;">Photo</div>'}
        </div>
        
        <!-- Name -->
        <div style="text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px;">${data.student?.full_name || 'Student Name'}</div>
        
        <!-- Info -->
        <div style="padding: 0 15px;">
          <table style="width: 100%; font-size: 8px;">
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.2);"><td style="opacity: 0.85; padding: 3px 0; width: 45%;">Student No</td><td style="padding: 3px 0;">${data.student?.student_no || 'GVS2024003'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.2);"><td style="opacity: 0.85; padding: 3px 0;">Course</td><td style="padding: 3px 0;">${data.student?.course || 'Class X'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.2);"><td style="opacity: 0.85; padding: 3px 0;">Session</td><td style="padding: 3px 0;">${data.student?.session || '2024-25'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.2);"><td style="opacity: 0.85; padding: 3px 0;">Father</td><td style="padding: 3px 0;">${data.student?.father_name || 'Mr. Singh'}</td></tr>
          </table>
        </div>
        
        <!-- QR Section -->
        <div style="position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);">
          ${data.qr_code ? `<div style="width: 45px; height: 45px; background: white; border-radius: 4px; overflow: hidden;"><img src="${data.qr_code}" style="width: 100%; height: 100%;"></div>` : '<div style="width:45px;height:45px;background:white;border-radius:4px;"></div>'}
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #064e3b 0%, #059669 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; color: white; position: relative;">
        <div style="position: absolute; top: 0; left: 0; width: 50px; height: 50px; background: #fbbf24; clip-path: polygon(0 0, 100% 0, 0 100%);"></div>
        <div style="padding: 20px 15px 15px; text-align: center;">
          <div style="color: #fbbf24; font-weight: 700; font-size: 11px; margin-bottom: 10px;">RULES & REGULATIONS</div>
          <div style="font-size: 7px; line-height: 1.6; text-align: left;">
            ${(settings.rules_regulations || '').split('\n').map(rule => `<div style="margin-bottom: 5px;">• ${rule}</div>`).join('')}
          </div>
        </div>
        <div style="padding: 0 15px; font-size: 8px; margin-top: 10px;">
          <div><strong style="color: #fbbf24;">Blood Group:</strong> ${data.student?.blood_group || 'N/A'}</div>
          <div><strong style="color: #fbbf24;">Phone:</strong> ${data.student?.phone || 'N/A'}</div>
          <div><strong style="color: #fbbf24;">Address:</strong> ${settings.college_address?.substring(0, 40) || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 15px; right: 15px; text-align: center;">
          ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 20px; filter: brightness(0) invert(1);">` : '<div style="width: 50px; border-top: 1px solid white; margin-bottom: 2px;"></div>'}
          <div style="font-size: 6px;">${settings.principal_name || 'Principal'}</div>
        </div>
      </div>
    `
  },

  // Template 8: Maroon Classic - Traditional Style
  'maroon-classic': {
    id: 'maroon-classic',
    name: 'Maroon Classic',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif; border: 3px solid #7c2d12;">
        <!-- Header -->
        <div style="background: #7c2d12; padding: 10px; text-align: center;">
          <div style="color: white; font-size: 11px; font-weight: 700;">${settings.college_name || "ST. MARY'S CONVENT"}</div>
          <div style="color: rgba(255,255,255,0.9); font-size: 7px;">Faith • Knowledge • Service</div>
        </div>
        
        <!-- Gold Divider -->
        <div style="height: 4px; background: linear-gradient(90deg, #fbbf24, #7c2d12, #fbbf24);"></div>
        
        <!-- Content -->
        <div style="padding: 12px;">
          <!-- Photo -->
          <div style="width: 65px; height: 80px; border: 3px solid #7c2d12; background: #f3f4f6; margin: 0 auto 10px; overflow: hidden;">
            ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:9px;">Photo</div>'}
          </div>
          
          <!-- Name -->
          <div style="text-align: center; font-size: 12px; font-weight: 700; color: #7c2d12; margin-bottom: 8px;">${data.student?.full_name || 'Student Name'}</div>
          
          <!-- Info -->
          <table style="width: 100%; font-size: 8px;">
            <tr style="border-bottom: 1px solid #eee;"><td style="color: #7c2d12; font-weight: 500; padding: 4px 0; width: 45%;">Admission No</td><td style="padding: 4px 0;">${data.student?.student_no || 'SMC2024007'}</td></tr>
            <tr style="border-bottom: 1px solid #eee;"><td style="color: #7c2d12; font-weight: 500; padding: 4px 0;">Class</td><td style="padding: 4px 0;">${data.student?.course || 'IX-C'}</td></tr>
            <tr style="border-bottom: 1px solid #eee;"><td style="color: #7c2d12; font-weight: 500; padding: 4px 0;">House</td><td style="padding: 4px 0;">${data.student?.house || 'Blue House'}</td></tr>
            <tr style="border-bottom: 1px solid #eee;"><td style="color: #7c2d12; font-weight: 500; padding: 4px 0;">DOB</td><td style="padding: 4px 0;">${data.student?.dob || '18/09/2010'}</td></tr>
          </table>
        </div>
        
        <!-- Footer -->
        <div style="position: absolute; bottom: -3px; left: -3px; right: -3px; background: #7c2d12; padding: 5px; color: white; text-align: center; font-size: 6px;">
          Faith • Knowledge • Service
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; border: 3px solid #7c2d12; position: relative;">
        <div style="background: #7c2d12; padding: 10px; text-align: center;">
          <div style="color: white; font-weight: 700; font-size: 10px;">RULES & REGULATIONS</div>
        </div>
        <div style="height: 4px; background: linear-gradient(90deg, #fbbf24, #7c2d12, #fbbf24);"></div>
        <div style="padding: 12px; font-size: 7px; line-height: 1.6;">
          ${(settings.rules_regulations || '').split('\n').map((rule, i) => `<div style="margin-bottom: 5px;">${i + 1}. ${rule.replace(/^\d+\.\s*/, '')}</div>`).join('')}
        </div>
        <div style="padding: 0 12px; font-size: 8px;">
          <div><strong style="color: #7c2d12;">Father's Name:</strong> ${data.student?.father_name || 'N/A'}</div>
          <div><strong style="color: #7c2d12;">Blood Group:</strong> ${data.student?.blood_group || 'N/A'}</div>
          <div><strong style="color: #7c2d12;">Session:</strong> ${data.student?.session || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 50px; right: 12px; text-align: center;">
          ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 20px;">` : '<div style="width: 50px; border-top: 1px solid #7c2d12; margin-bottom: 2px;"></div>'}
          <div style="font-size: 6px; color: #7c2d12;">${settings.principal_name || 'Principal'}</div>
        </div>
        <div style="position: absolute; bottom: 45px; left: 12px;">
          ${data.qr_code ? `<img src="${data.qr_code}" style="width: 40px; height: 40px;">` : ''}
        </div>
        <div style="position: absolute; bottom: -3px; left: -3px; right: -3px; background: #7c2d12; padding: 5px; color: white; text-align: center; font-size: 6px;">
          📞 ${settings.emergency_contact || 'Emergency Contact'}
        </div>
      </div>
    `
  },

  // Template 9: Pink Gradient - Soft & Elegant
  'pink-gradient': {
    id: 'pink-gradient',
    name: 'Pink Gradient',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #be185d 0%, #ec4899 50%, #f9a8d4 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif; color: white;">
        <!-- Header -->
        <div style="padding: 12px; text-align: center;">
          <div style="width: 35px; height: 35px; background: rgba(255,255,255,0.2); border-radius: 50%; margin: 0 auto 5px; overflow: hidden;">
            ${settings.college_logo ? `<img src="${settings.college_logo}" style="width:100%;height:100%;object-fit:cover;">` : ''}
          </div>
          <div style="font-size: 11px; font-weight: 700;">${settings.college_name || 'BLOSSOM INTERNATIONAL'}</div>
          <div style="font-size: 7px; opacity: 0.9;">Where Dreams Take Flight</div>
        </div>
        
        <!-- Photo -->
        <div style="width: 65px; height: 80px; border: 3px solid white; border-radius: 8px; background: rgba(255,255,255,0.15); margin: 5px auto 8px; overflow: hidden;">
          ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.7);font-size:9px;">Photo</div>'}
        </div>
        
        <!-- Name -->
        <div style="text-align: center; font-size: 12px; font-weight: 700;">${data.student?.full_name || 'Student Name'}</div>
        
        <!-- Info Section -->
        <div style="background: rgba(255,255,255,0.15); margin: 8px 12px; padding: 8px; border-radius: 8px;">
          <table style="width: 100%; font-size: 8px;">
            <tr><td style="opacity: 0.9; padding: 2px 0; width: 40%;">ID</td><td style="padding: 2px 0;">${data.student?.student_no || 'BIS2024008'}</td></tr>
            <tr><td style="opacity: 0.9; padding: 2px 0;">Grade</td><td style="padding: 2px 0;">${data.student?.course || '7th Standard'}</td></tr>
            <tr><td style="opacity: 0.9; padding: 2px 0;">Section</td><td style="padding: 2px 0;">${data.student?.section || 'Orchid'}</td></tr>
            <tr><td style="opacity: 0.9; padding: 2px 0;">Blood</td><td style="padding: 2px 0;">${data.student?.blood_group || 'B-'}</td></tr>
          </table>
        </div>
        
        <!-- Footer -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0; background: rgba(0,0,0,0.2); padding: 5px; text-align: center; font-size: 6px;">
          📞 Emergency: ${settings.emergency_contact || '1800-XXX-XXXX'}
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #be185d 0%, #ec4899 50%, #f9a8d4 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; color: white; position: relative;">
        <div style="padding: 15px; text-align: center;">
          <div style="font-weight: 700; font-size: 11px; margin-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,0.3); padding-bottom: 6px;">RULES & REGULATIONS</div>
          <div style="font-size: 7px; line-height: 1.6; text-align: left;">
            ${(settings.rules_regulations || '').split('\n').map(rule => `<div style="margin-bottom: 5px;">• ${rule}</div>`).join('')}
          </div>
        </div>
        <div style="background: rgba(255,255,255,0.15); margin: 10px 15px; padding: 8px; border-radius: 8px; font-size: 8px;">
          <div><strong>Father:</strong> ${data.student?.father_name || 'N/A'}</div>
          <div><strong>Session:</strong> ${data.student?.session || 'N/A'}</div>
          <div><strong>Valid Till:</strong> ${data.valid_until?.substring(0, 4) || '2027'}</div>
        </div>
        <div style="position: absolute; bottom: 55px; right: 15px; text-align: center;">
          ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 18px; filter: brightness(0) invert(1);">` : '<div style="width: 45px; border-top: 1px solid white; margin-bottom: 2px;"></div>'}
          <div style="font-size: 6px;">${settings.principal_name || 'Principal'}</div>
        </div>
        <div style="position: absolute; bottom: 15px; left: 50%; transform: translateX(-50%);">
          ${data.qr_code ? `<div style="width: 45px; height: 45px; background: white; border-radius: 4px; overflow: hidden;"><img src="${data.qr_code}" style="width: 100%; height: 100%;"></div>` : ''}
        </div>
      </div>
    `
  },

  // Template 10: Dark Premium Gold - Luxury Design
  'dark-premium': {
    id: 'dark-premium',
    name: 'Dark Premium Gold',
    orientation: 'vertical',
    renderFront: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #18181b 0%, #27272a 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); position: relative; font-family: 'Poppins', sans-serif; color: white; border: 2px solid #fbbf24;">
        <!-- Gold Line -->
        <div style="height: 2px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
        
        <!-- Header -->
        <div style="padding: 12px; text-align: center;">
          <div style="color: #fbbf24; font-size: 11px; font-weight: 700;">${settings.college_name || 'ELITE ACADEMY'}</div>
          <div style="font-size: 7px; opacity: 0.7;">Premium Education Institute</div>
        </div>
        
        <!-- Gold Line -->
        <div style="height: 2px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
        
        <!-- Photo -->
        <div style="width: 65px; height: 80px; border: 2px solid #fbbf24; background: rgba(255,255,255,0.05); margin: 10px auto 8px; overflow: hidden;">
          ${data.student?.photo ? `<img src="${data.student.photo}" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.5);font-size:9px;">Photo</div>'}
        </div>
        
        <!-- Name & ID -->
        <div style="text-align: center; font-size: 12px; font-weight: 700;">${data.student?.full_name || 'Student Name'}</div>
        <div style="text-align: center; color: #fbbf24; font-size: 8px;">${data.student?.student_no || 'EA-PREM-2024009'}</div>
        
        <!-- Info -->
        <div style="padding: 8px 15px;">
          <table style="width: 100%; font-size: 8px;">
            <tr style="border-bottom: 1px solid rgba(251,191,36,0.2);"><td style="color: #fbbf24; padding: 3px 0; width: 40%;">Program</td><td style="padding: 3px 0;">${data.student?.course || 'IB Diploma'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(251,191,36,0.2);"><td style="color: #fbbf24; padding: 3px 0;">Year</td><td style="padding: 3px 0;">${data.student?.session || '2024-26'}</td></tr>
            <tr style="border-bottom: 1px solid rgba(251,191,36,0.2);"><td style="color: #fbbf24; padding: 3px 0;">Blood</td><td style="padding: 3px 0;">${data.student?.blood_group || 'A-'}</td></tr>
          </table>
        </div>
        
        <!-- QR & Signature Footer -->
        <div style="position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 15px;">
          ${data.qr_code ? `<div style="width: 35px; height: 35px; background: white; border-radius: 3px; overflow: hidden;"><img src="${data.qr_code}" style="width: 100%; height: 100%;"></div>` : '<div style="width:35px;height:35px;background:white;border-radius:3px;"></div>'}
          <div style="text-align: center;">
            ${settings.principal_signature ? `<img src="${settings.principal_signature}" style="height: 18px; filter: brightness(0) invert(1);">` : '<div style="width: 45px; border-top: 1px solid #fbbf24; margin-bottom: 2px;"></div>'}
            <div style="font-size: 6px; color: #fbbf24;">${settings.principal_name || 'Director'}</div>
          </div>
        </div>
      </div>
    `,
    renderBack: (data, settings) => `
      <div style="width: 204px; height: 324px; background: linear-gradient(180deg, #18181b 0%, #27272a 100%); border-radius: 10px; overflow: hidden; box-shadow: 0 15px 40px rgba(0,0,0,0.3); font-family: 'Poppins', sans-serif; color: white; border: 2px solid #fbbf24; position: relative;">
        <div style="height: 2px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
        <div style="padding: 15px; text-align: center;">
          <div style="color: #fbbf24; font-weight: 700; font-size: 11px; margin-bottom: 10px;">TERMS & CONDITIONS</div>
          <div style="font-size: 7px; line-height: 1.6; text-align: left; opacity: 0.9;">
            ${(settings.rules_regulations || '').split('\n').map(rule => `<div style="margin-bottom: 5px;">• ${rule}</div>`).join('')}
          </div>
        </div>
        <div style="padding: 0 15px; font-size: 8px;">
          <div><strong style="color: #fbbf24;">Father:</strong> ${data.student?.father_name || 'N/A'}</div>
          <div><strong style="color: #fbbf24;">Contact:</strong> ${data.student?.phone || 'N/A'}</div>
          <div><strong style="color: #fbbf24;">Emergency:</strong> ${settings.emergency_contact || 'N/A'}</div>
        </div>
        <div style="position: absolute; bottom: 15px; left: 50%; transform: translateX(-50%); text-align: center;">
          <div style="color: #fbbf24; font-size: 8px; font-weight: 600;">${settings.college_name || 'ELITE ACADEMY'}</div>
          <div style="font-size: 6px; opacity: 0.7;">${settings.college_address?.substring(0, 40) || 'Premium Education Institute'}</div>
        </div>
        <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, #fbbf24, transparent);"></div>
      </div>
    `
  }
};

const PrintICardProfessional = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('blue-curve');
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchStudents();
    fetchSettings();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students/dropdown');
      setStudents(response.data.students || response.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/icard-presets/settings');
      setSettings(response.data);
      if (response.data?.selected_template && ICardTemplates[response.data.selected_template]) {
        setSelectedTemplate(response.data.selected_template);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const generateCardData = async (studentIds) => {
    if (studentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    const data = [];
    for (const id of studentIds) {
      try {
        const response = await api.get(`/icard-presets/student-data/${id}`);
        data.push(response.data);
      } catch (error) {
        console.error(`Error fetching data for student ${id}:`, error);
      }
    }
    setCardData(data);
    setShowPreview(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print I-Cards</title>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
          <style>
            @page { size: A4; margin: 10mm; }
            body { margin: 0; padding: 0; font-family: 'Poppins', sans-serif; }
            .card-container { display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; padding: 10px; }
            .card-pair { display: flex; gap: 10px; margin-bottom: 15px; page-break-inside: avoid; }
            @media print {
              .card-pair { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const filteredStudents = students.filter(s => 
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.admission_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const template = ICardTemplates[selectedTemplate] || ICardTemplates['blue-curve'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Printer className="h-7 w-7 text-blue-600" />
          Print I-Cards (Professional)
        </h1>
        <p className="text-gray-600 mt-1">Select students and template to generate professional ID cards</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Student Selection */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="search-students-input"
              />
            </div>
            <button
              onClick={() => setSelectedStudents(filteredStudents.map(s => s.id))}
              className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
              data-testid="select-all-btn"
            >
              Select All
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-lg">
            {filteredStudents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No students found</div>
            ) : (
              filteredStudents.map(student => (
                <label 
                  key={student.id} 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b"
                  data-testid={`student-item-${student.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedStudents([...selectedStudents, student.id]);
                      } else {
                        setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                      }
                    }}
                    className="rounded text-blue-600"
                  />
                  <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {student.photo ? (
                      <img src={student.photo} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <Users className="w-full h-full p-2 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{student.full_name}</div>
                    <div className="text-sm text-gray-500">{student.admission_number} • {student.course_name}</div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">{selectedStudents.length} students selected</span>
            <button
              onClick={() => generateCardData(selectedStudents)}
              disabled={selectedStudents.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="generate-cards-btn"
            >
              <Eye className="h-4 w-4" />
              Generate Cards
            </button>
          </div>
        </div>

        {/* Right - Template Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Select Template</h3>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {Object.values(ICardTemplates).map(t => (
              <label 
                key={t.id} 
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  selectedTemplate === t.id 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'hover:border-gray-300'
                }`}
                data-testid={`template-${t.id}`}
              >
                <input
                  type="radio"
                  name="template"
                  checked={selectedTemplate === t.id}
                  onChange={() => setSelectedTemplate(t.id)}
                  className="text-blue-600"
                />
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-gray-500 capitalize">{t.orientation}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && cardData.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold text-lg">Preview I-Cards ({cardData.length} cards)</h3>
              <div className="flex gap-2">
                <button 
                  onClick={handlePrint} 
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  data-testid="print-all-btn"
                >
                  <Printer className="h-4 w-4" /> Print All
                </button>
                <button 
                  onClick={() => setShowPreview(false)} 
                  className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  data-testid="close-preview-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[75vh] bg-gray-100" ref={printRef}>
              <div className="card-container flex flex-wrap gap-6 justify-center">
                {cardData.map((data, index) => (
                  <div key={index} className="card-pair flex gap-4 bg-white p-4 rounded-lg shadow">
                    {/* Front */}
                    <div dangerouslySetInnerHTML={{ __html: template.renderFront(data, settings || {}) }} />
                    {/* Back */}
                    <div dangerouslySetInnerHTML={{ __html: template.renderBack(data, settings || {}) }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintICardProfessional;
